/**
 * #402 — server-authoritative decision for `/dashboard/self-pay`.
 *
 * The production incident: a driver with valid company-funded Hours coverage
 * (`state=active`, `is_self_pay=false`, `payment_action_required=false`) was
 * trapped on "Pague a sua própria licença" with no exit, being invited to buy a
 * second license. This module is the single pure decision used by the screen so
 * that entitlement is resolved BEFORE any trial/purchase CTA is exposed.
 *
 * Surfaces:
 *   - `redirect-home`    the identity already holds valid effective Hours
 *                        coverage — personal (D-007 reuse, D-036 paid period)
 *                        or company-funded (D-031 operation without a personal
 *                        seat, D-053 one effective payer). Route home; never
 *                        show a personal trial/plan/card/Checkout action, and
 *                        never relabel company funding as personal self-pay.
 *   - `payment-recovery` the driver's own personal license needs payment action
 *                        (`past_due`/`unpaid`/`incomplete`). Keep the existing
 *                        recovery boundary (#405 / paywall-expired) — never
 *                        silently convert it into a new trial/purchase (D-049).
 *   - `fail-closed`      entitlement unresolved (loading or unknown/network
 *                        failure). Never a purchasable default; the screen
 *                        offers retry + exit only.
 *   - `purchase`         genuine uncovered self-pay: catalog + intentional
 *                        checkout (D-007 allows accepting before purchasing).
 */
import type { PaywallStatusResponse } from '../types/paywall';

export type SelfPaySurface =
  | { kind: 'redirect-home'; reason: 'personal-active' | 'company-active' }
  | { kind: 'payment-recovery' }
  | { kind: 'fail-closed'; retryable: boolean }
  | { kind: 'purchase' };

/**
 * States a driver with no effective coverage may legitimately purchase from
 * (D-007 acceptance-before-purchase, D-014/D-015 company coverage ended, Q6
 * company grace). This is an explicit allowlist: any state not listed — an
 * unrecognised/future payload, `past_due`, `paused`, empty, etc. — must NOT
 * fall through to a purchasable surface.
 */
const PURCHASABLE_STATES: ReadonlySet<string> = new Set([
  'no_subscription',
  'blocked',
  'company_grace',
]);

export function deriveSelfPaySurface(
  status: PaywallStatusResponse | null,
  isUnknown: boolean,
): SelfPaySurface {
  // Fail closed: an unresolved entitlement is never treated as "no
  // subscription". Treating unknown as uncovered is what invites a duplicate
  // purchase.
  if (isUnknown || !status) {
    return { kind: 'fail-closed', retryable: true };
  }

  // Any effective coverage means the self-pay purchase surface must not render.
  // `active`/`trialing` with `is_self_pay === false` is a company-funded (or
  // owner's own company) entitlement, NOT a coverless driver.
  if (status.state === 'active' || status.state === 'trialing') {
    return status.is_self_pay === true
      ? { kind: 'redirect-home', reason: 'personal-active' }
      : { kind: 'redirect-home', reason: 'company-active' };
  }

  // Payment trouble on the driver's own license ("recovery boundary"), never a
  // fresh trial.
  if (status.payment_action_required === true) {
    return { kind: 'payment-recovery' };
  }

  // An expired self-pay trial (no payment trouble) is a genuine uncovered
  // driver who may intentionally repurchase.
  if (status.state === 'trial_expired' && status.is_self_pay === true) {
    return { kind: 'purchase' };
  }

  // Explicit allowlist. A state we do not positively recognise fails closed
  // rather than defaulting to checkout — the failure mode that trapped drivers
  // before #402.
  if (PURCHASABLE_STATES.has(status.state)) {
    return { kind: 'purchase' };
  }

  return { kind: 'fail-closed', retryable: true };
}
